import * as React from 'react';
import { observer } from 'mobx-react';
import * as d3 from 'd3';

import { Token } from '../stores/Interfaces';
import { Question } from '../stores/Question';
import { Paragraph } from '../stores/Paragraph';

import { Answer } from '../stores/Answer';

import { utils } from '../stores/Utils';
import '../css/App.css';

export interface FlowmapProps {
    question: Question;
    paragraph: Paragraph;
    attention: number[][];
    selectedToken: Token|null;
    selectTokenIdxHandler: (idx: number) => void;
    mouseoutTokenIdxHandler: () => void;
    mouseoveredHandler: (idx: number) => void;
    mouseoutHandler: () => void;
    answers: Answer[];
}

@observer
export class Flowmap extends React.Component<FlowmapProps, {}> {
    private containerId: string;
    private margin: {top: number, right: number; bottom: number; left: number; };
    private size: {svgHeight: number; svgWidth: number; width: number; height: number; };
    private scale: {
        barHeight: number;
        colorScale: d3.ScaleQuantile<string>;
        // for the line chart
        xScale: d3.ScaleLinear<number, number>;
        yScale: d3.ScaleLinear<number, number>;
        // for the glyphs
        tScale: d3.ScaleOrdinal<string, number>;
        pScale: d3.ScaleLinear<number, number>;
        wScale: d3.ScaleOrdinal<number, number>;
        qScale: d3.ScaleOrdinal<number, number>;
    };    

    private node: any;
    private svg: any; //d3.Selection<any, number, any, {}>; // svg
    private panel: d3.Selection<any, number, any, {}>; // main part

    private pAttention: number[][];
    private qAttention: number[][];

    constructor(props: any, context: any) {
        super(props, context);
        this.margin = { top: 2, right: 10, bottom: 2, left: 10 };
        this.size = { svgWidth: 0, width: 0, height: 0, svgHeight: 0 };
        this.containerId = `question-flowmap-${this.props.question.qid}`;
    }

    public componentDidMount() {
        this.svg = d3.select(this.node);        
        if (this.props.attention.length === 0) {
            return;
        }
        this.qAttention = this.props.attention;
        this.pAttention = utils.transpose(this.props.attention);
        this.init();
        this.node.style.width = `${this.size.svgWidth}px`;
        this.node.style.height = `${this.size.svgHeight}px`;
        this.panel = this.svg.append('g').attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);
        this.genScales();
        this.renderGlyphs(this.pAttention, 'paragraph');
        this.renderGlyphs(this.qAttention, 'question');
        this.renderLinks(this.props.selectedToken);
        this.renderAnswers();
     }

     public componentDidUpdate() {
        if (this.props.attention.length === 0) {
            return;
        }
        this.qAttention = this.props.attention;
        this.pAttention = utils.transpose(this.props.attention);
        this.init();
        this.node.style.width = `${this.size.svgWidth}px`;
        this.node.style.height = `${this.size.svgHeight}px`;
        this.panel.selectAll('*').remove();
        this.panel.attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);
        this.genScales();
        this.renderGlyphs(this.pAttention, 'paragraph');
        this.renderGlyphs(this.qAttention, 'question');
        this.renderLinks(this.props.selectedToken);
        this.renderAnswers();
     }

    /**
     * Rendering function called whenever new logs are generated.
     */
    public render(): JSX.Element {
        return <svg ref={node => this.node = node}
            width={this.size.svgWidth} height={this.size.svgHeight}>
        </svg>
     }
    

    private init(): void {
        const container = document.getElementById(this.containerId);
        this.size.svgWidth = container === null || container.clientWidth === 0 ? 200 : container.clientWidth;
        this.size.svgHeight = container === null ? 100 : container.clientHeight;

        this.size.width = this.size.svgWidth - this.margin.right - this.margin.left;
        this.size.height = this.size.svgHeight - this.margin.top - this.margin.bottom;
    }

    /**
     * Build the scale
     */
    private genScales(): void {
        const barHeight = 10;
        let qScaleRange: number[] = [];
        const widthRange: number[] = [];
        this.props.question.doc.forEach(t => {
            const id = utils.genClass('qpanel', 'qtoken', [this.props.question.qid, utils.genStrId(t.text), t.idx]).key
            const rect = document.getElementById(id);//.getBoundingClientRect();
            if (rect) {
                qScaleRange.push(rect.getBoundingClientRect().left);
                widthRange.push(rect.getBoundingClientRect().width);
            };
        });
        qScaleRange = qScaleRange.map(r => r - qScaleRange[0] - this.margin.left);
        if (!this.scale) {
            this.scale = {
                barHeight: barHeight,
                xScale: d3.scaleLinear<number, number>(),
                yScale: d3.scaleLinear<number, number>().domain([0, 1.01]).range([barHeight, 0]),
                tScale: d3.scaleOrdinal<string, number>().domain(['question', 'paragraph']),
                pScale: d3.scaleLinear<number, number>().range([0, this.size.width]),
                qScale: d3.scaleOrdinal<number, number>().range(qScaleRange),
                wScale: d3.scaleOrdinal<number, number>().range(widthRange),
                colorScale: d3.scaleQuantile<string>().domain([0, 1.01]).range(utils.attentionColor.others)
            };
        }
        this.scale.pScale.domain([0, this.pAttention.length]);
        this.scale.qScale.domain(d3.range(0, this.qAttention.length));
        this.scale.wScale.domain(d3.range(0, this.qAttention.length));

        this.scale.tScale.range([0, this.size.height - barHeight]);
        const maxRange = (this.scale.qScale(1) - this.scale.qScale(0)) * 0.9;
        this.scale.xScale.range([0, maxRange]).domain([0, this.pAttention.length]);
    }

    private renderGlyphs(attention: number[][], gtype: string): void {
        this.panel.selectAll(`.glyph-${gtype}`).remove();
        if (gtype !== 'question' && gtype !== 'paragraph') { return; }
        const scale: any = gtype === 'question' ? this.scale.qScale : this.scale.pScale;
        const attentionPlot = gtype === 'paragraph' && this.props.selectedToken !== null ?
        this.qAttention[this.props.selectedToken.idx] : attention.map(a => d3.max(a));
        const glyphs = this.panel.selectAll(`.glyph-${gtype}`).data(attentionPlot);
        const glyphEntry = glyphs.enter().append('g')
            .attr('class', (d, idx) => `glyph glyph-${gtype} glyph-${gtype}-${idx}`)
            .attr('transform', (d, idx: number) => `translate(${scale(idx)}, ${this.scale.tScale(gtype)})`)
            
        glyphEntry.append('rect')
            .attr('x', 0).attr('y', 0)
            .attr('width', (d, idx: number) => {
                return gtype === 'question' ? this.scale.wScale(idx) - 3 : 0.9 * (scale(idx+1) - scale(idx))
            } )
            .attr('height', this.scale.barHeight)
            .attr('fill', (d: number, idx: number) => {
                return d === 0 ? 'white' : this.scale.colorScale(d);
            })
            .attr('stroke', 'lightgrey')
            .attr('stroke-width', (d: number, idx: number) => {
                return (gtype === 'question' && this.props.selectedToken !== null && idx === this.props.selectedToken.idx) || d === 0 ? 1 : 0
            }) 
            .on('mouseout', (d, idx) =>{
                //
                if (gtype === 'paragraph') {
                    this.props.mouseoutHandler()
                } else {
                    this.props.mouseoutTokenIdxHandler();
                    this.renderLinks(null);
                }
                //d3.select(`.glyph-${gtype}-${idx}`).attr('stroke', 'white').attr('stroke-width', 0)
            })
            .on('mouseover', (d, idx) => {
                if (gtype === 'paragraph') {
                    this.props.mouseoveredHandler(idx);
                } else if (gtype === 'question') {
                    this.props.selectTokenIdxHandler(idx);
                    this.renderLinks(this.props.question.doc[idx]);
                }
                //
                //d3.select(`.glyph-${gtype}-${idx}`).attr('stroke', 'red').attr('stroke-width', 2);
            })
        /*
        if(gtype === 'question') {
            glyphEntry.append('path')
                .attr('class', 'sparkline')
                .attr('fill', 'none')
                .attr('stroke', 'steelblue')
                .attr('stroke-linejoin', 'round')
                .attr('stroke-linecap', 'round')
                .attr('d', (d, idx: number) => (line(attention[idx])))
        }
        */
        
        glyphs.transition()
            .attr('fill', (d: number) => this.scale.colorScale(d))
        glyphs.exit().transition().remove();
    }

    private renderLinks(selectedToken: Token|null): void {

        const drawAttention: {qtoken: number, ptoken: number, attention: number}[] = [];
        this.qAttention
            .forEach((attention, qtokenIdx) => {
                attention
                    .map((a, idx) => {return {ptoken: idx, attention: a} })
                    .filter(a => a.attention > 0.5 && (selectedToken === null || selectedToken === undefined || selectedToken.idx === qtokenIdx))
                    .forEach(a => {
                        drawAttention.push({qtoken: qtokenIdx, ptoken: a.ptoken, attention: a.attention})
                    });
            });
        const dpx = this.scale.pScale(1) - this.scale.pScale(0);
        /*
        const linkFunc = (d:{qtoken: number, ptoken: number, attention: number}) => 
            `M ${this.scale.qScale(d.qtoken) + this.scale.xScale.range()[1] / 2} ${this.scale.tScale('question') + this.scale.barHeight} ` + 
            `L ${this.scale.qScale(d.qtoken) + this.scale.xScale.range()[1] / 2 + dx} ${this.scale.tScale('question')  + this.scale.barHeight}` + 
            `L ${this.scale.pScale(d.ptoken+1)} ${this.scale.tScale('paragraph')}` +
            `L ${this.scale.pScale(d.ptoken)} ${this.scale.tScale('paragraph')} Z`;
        */
        const diagnalFunc = (d:{qtoken: number, ptoken: number, attention: number}) => {
            const source = {x: this.scale.qScale(d.qtoken) + this.scale.wScale(d.qtoken) / 2, y: this.scale.tScale('question')  + this.scale.barHeight};
            const target = {x: this.scale.pScale(d.ptoken) + dpx / 2, y: this.scale.tScale('paragraph')};
            return "M" + target.x + "," + target.y
                + "C" + target.x + "," + (target.y + source.y) / 2
                + " " + source.x + "," +  (target.y + source.y) / 2
                + " " + source.x + "," + source.y;
        }
        
        const links = this.panel.selectAll('.attention-links').data(drawAttention);
        
        links.enter().append('path')
            .attr('d', d => diagnalFunc(d))
            .attr('stroke-width', dpx > 3 ? 3 : dpx )
            .attr('class', 'attention-links')
            .attr('fill', 'none')
            .attr('stroke', d => this.props.question.doc[d.qtoken].lemma === this.props.paragraph.doc[d.ptoken].lemma ? '#fdd0a2' : 'lightgrey')
            .style('opacity', 0.5)
        links
            .attr('d', d => diagnalFunc(d))
            .attr('stroke-width', dpx > 3 ? 3 : dpx )
        /*
        links.enter().append('path')
            .attr('d', d => linkFunc(d))
            .style('opacity', 0.3)
            .attr('class', 'attention-links')
            .attr('fill', 'lightgrey');
        links.attr('d', d => linkFunc(d));
        */
        links.exit().transition().remove();

    }
    

    private renderAnswers(): void {
        if (this.panel.selectAll('.answerRect').empty()) {
            this.panel.selectAll('.answerRect').data(this.props.answers).enter()
                .append('line')
                .attr('stroke', a => a.getColorDark())
                .attr('x1', a => this.scale.pScale(a.span_start))
                .attr('x2', a => this.scale.pScale(a.span_end))
                .attr('y1', this.size.height)
                .attr('y2', this.size.height)
                .attr('class', 'answerRect')
                .attr('stroke-width', 5);
        }

    }
    /*
    private renderStroke(): void {
        if (this.panel.selectAll('.stroke').empty()) {
            this.panel.append('rect')
                .datum(this.attention)
                .attr('fill', 'none')
                .attr('stroke', 'lightgrey')
                .attr('stroke-width', 1)
                .attr('x', 0)
                .attr('y', 0)
                .attr('class', 'stroke');
        }
        this.panel.selectAll('.stroke')
            .attr('width', this.size.width)
            .attr('height', this.size.height);
    }

    private mousemove(x: number|null, obj: any, self: Sparkline): void {
        if (x === null) {
            x = Math.floor(self.scale.xScale.invert(d3.mouse(obj)[0]));
            this.mouseoveredHandler(x);
        }
        this.panel.selectAll('.focus')
            .attr('transform', `translate(${self.scale.xScale(x)}, ${self.scale.yScale(self.attention[x])})`)
            .select('text').text(self.attention[x].toFixed(2));
      }
      */
}    


// WEBPACK FOOTER //
// ./src/components/Flowmap.tsx